import { sum, sumBy } from 'lodash';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { MatcherName, registerLocallyHandledErrorMatcher, singleErrorMatcher, } from 'api/locallyHandledErrors';
import useDebouncedCallbackPerKey from 'hooks/useDebounceCallbackPerKey';
import CheckIcon from 'images/v5/icons/check.svg';
import ChevronDownSvg from 'images/v5/icons/chevron-down.svg';
import EditSvg from 'images/v5/icons/edit-02.svg';
import MinusIcon from 'images/v5/icons/minus.svg';
import PlusSvg from 'images/v5/icons/plus.svg';
import Button from 'layout/atoms/Button/Button';
import Checkbox from 'layout/atoms/Checkbox/Checkbox';
import Dropdown from 'layout/atoms/Dropdown/Dropdown';
import Header from 'layout/atoms/Header/Header';
import InputNumber from 'layout/atoms/InputNumber';
import Loader from 'layout/atoms/Loader/Loader';
import TableGrid, { Body, Cell, Head, Row } from 'layout/atoms/TableGrid';
import EmptyState from 'layout/molecules/EmptyState/EmptyState';
import Grid from 'layout/molecules/Grid/Grid';
import { openModal } from 'store/layoutSlice';
import { fetchSpecificationProblems, selectStepValidationRequestId, } from 'store/specificationDetailsSlice';
import { changeSizeStandard, deactivateSize, fetchSizings, fetchSpecificationProductionQuantities, removeModelVariants, selectProductionQuantitiesItem, selectProductionQuantitiesRefetchFlags, selectProductionQuantitiesStatus, updateModelVariants, updateStoreModelVariants, } from 'store/specificationProductionQuantitiesSlice';
import { addToast } from 'store/toastsSlice';
import exceptionMessageToKey from 'utils/exceptionMessage';
registerLocallyHandledErrorMatcher(MatcherName.RemoveModelColorsException, singleErrorMatcher({ message: 'FashionappException', operationName: 'RemoveModelColors' }));
function ProductionQuantities() {
    var _a;
    const { t } = useTranslation(['specification', 'common']);
    const dispatch = useDispatch();
    const specificationDetails = useSelector((state) => state.specificationDetails.item);
    const sizings = useSelector((state) => state.specificationProductionQuantities.sizings.items);
    const productionQuantitiesStatus = useSelector(selectProductionQuantitiesStatus);
    const productionQuantitiesRefetchFlags = useSelector(selectProductionQuantitiesRefetchFlags);
    const productionQuantitiesItem = useSelector(selectProductionQuantitiesItem);
    const sizeStandard = (_a = productionQuantitiesItem === null || productionQuantitiesItem === void 0 ? void 0 : productionQuantitiesItem.sizeStandard) !== null && _a !== void 0 ? _a : null;
    const sizes = productionQuantitiesItem === null || productionQuantitiesItem === void 0 ? void 0 : productionQuantitiesItem.sizes;
    const colors = productionQuantitiesItem === null || productionQuantitiesItem === void 0 ? void 0 : productionQuantitiesItem.colors;
    const variants = productionQuantitiesItem === null || productionQuantitiesItem === void 0 ? void 0 : productionQuantitiesItem.variants;
    const [selectedVariants, setSelectedVariants] = useState([]);
    const [showErrors, setShowErrors] = useState(false);
    const stepValidationRequestId = useSelector(selectStepValidationRequestId);
    useEffect(() => {
        if (stepValidationRequestId > 0) {
            setShowErrors(true);
        }
    }, [stepValidationRequestId]);
    useEffect(() => {
        dispatch(fetchSizings());
        if (productionQuantitiesStatus.fetch === 'idle' || productionQuantitiesRefetchFlags.fetch) {
            dispatch(fetchSpecificationProductionQuantities({ specificationId: specificationDetails.id }));
        }
    }, [
        dispatch,
        specificationDetails.id,
        productionQuantitiesStatus,
        productionQuantitiesRefetchFlags,
    ]);
    const sortVariants = (sizes, variantsRow) => {
        const idOrderMap = new Map();
        sizes.forEach((item, index) => {
            idOrderMap.set(item.id, index);
        });
        return variantsRow.sort((a, b) => {
            var _a, _b;
            const indexA = (_a = idOrderMap.get(a.size.id)) !== null && _a !== void 0 ? _a : Infinity;
            const indexB = (_b = idOrderMap.get(b.size.id)) !== null && _b !== void 0 ? _b : Infinity;
            return indexA - indexB;
        });
    };
    const [debouncedUpdateModelVariants, savingVariantKeys] = useDebouncedCallbackPerKey((variantId, value) => {
        dispatch(updateModelVariants({ variantId, value }))
            .unwrap()
            .then(() => {
            dispatch(fetchSpecificationProblems({ specificationId: specificationDetails.id }));
        });
    }, 1000);
    const handleSizeStandardChange = (specificationId, sizeStandardId) => {
        dispatch(changeSizeStandard({ specificationId, sizeStandardId }));
    };
    const handleSelectVariant = (id) => selectedVariants.includes(id)
        ? setSelectedVariants(selectedVariants.filter((variantId) => variantId !== id))
        : setSelectedVariants([...selectedVariants, id]);
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        dispatch(updateStoreModelVariants({ variantId: name, value }));
        debouncedUpdateModelVariants(name, value);
    };
    if (productionQuantitiesStatus.fetch === 'idle' || productionQuantitiesItem === null) {
        return React.createElement(Loader, null);
    }
    // sizeStandard is nullable in the schema - a specification can exist before one is picked.
    const sizeStandardLabel = sizeStandard ? t(sizeStandard.name) : t('size_standard_not_selected');
    return (React.createElement(Grid, { gap: 24 },
        React.createElement(Header, { size: "m", aside: specificationDetails.state === 'PRODUCTION' ? (React.createElement("div", null,
                React.createElement(Button, { theme: "border", rightIcon: React.createElement(ChevronDownSvg, null), disabled: true },
                    t('size_standard'),
                    ": ",
                    sizeStandardLabel))) : (React.createElement(Dropdown, { position: "bottomLeft", trigger: React.createElement(Button, { theme: "border", rightIcon: React.createElement(ChevronDownSvg, null) },
                    t('size_standard'),
                    ": ",
                    sizeStandardLabel) }, sizings.map((sizing) => {
                const isSelected = (sizeStandard === null || sizeStandard === void 0 ? void 0 : sizeStandard.id) === sizing.id;
                return (React.createElement(Button, { key: sizing.id, align: "left", theme: isSelected ? 'fill' : 'ghost', leftIcon: isSelected ? React.createElement(CheckIcon, null) : undefined, onClick: () => handleSizeStandardChange(specificationDetails.id, sizing.id) },
                    t(sizing.name),
                    sizing.isDefault && ` (${t('default_size_standard_current').toLowerCase()})`));
            }))) }, t('production_quantities')),
        sizeStandard === null ? (React.createElement(EmptyState, { header: t('no_size_standard_header'), subheader: t('no_size_standard_subheader') })) : (React.createElement(Grid, { gap: 0 },
            React.createElement(TableGrid, { columns: [
                    ['180px', '1fr'],
                    ...sizes
                        .filter((size) => size.isActive)
                        .map(() => ['80px', '150px']),
                    ['40px', '40px'],
                    ['80px', '150px'],
                ], columnBorders: true },
                React.createElement(Head, null,
                    React.createElement(Row, { head: true },
                        React.createElement(Cell, null, t('common:color_version')),
                        sizes.map((size) => {
                            var _a;
                            return size.isActive && (React.createElement(Cell, { key: size.id }, (_a = size.sizeName) === null || _a === void 0 ? void 0 :
                                _a.name,
                                ' ',
                                specificationDetails.state !== 'PRODUCTION' && (React.createElement("aside", null,
                                    React.createElement(Button, { size: "xs", shape: "rounded", leftIcon: React.createElement(MinusIcon, null), onClick: () => dispatch(deactivateSize({ specificationSizeId: size.id })) })))));
                        }),
                        React.createElement(Cell, null,
                            React.createElement(Button, { size: "xs", shape: "rounded", leftIcon: React.createElement(PlusSvg, null), onClick: () => dispatch(openModal({ name: 'sizesEditor' })), disabled: specificationDetails.state === 'PRODUCTION' })),
                        React.createElement(Cell, null, t('common:summary')))),
                React.createElement(Body, null, colors.map((color) => {
                    return (React.createElement(Row, { key: color.id },
                        React.createElement(Cell, null, specificationDetails.state !== 'PRODUCTION' ? (React.createElement("span", { style: { display: 'flex', gap: '6px' } },
                            React.createElement(Checkbox, { label: color.name, value: selectedVariants.includes(color.id), onClick: () => handleSelectVariant(color.id) }),
                            specificationDetails.state === 'DRAFT' && (React.createElement(Button, { size: "xs", shape: "squared", theme: "ghost", leftIcon: React.createElement(EditSvg, null), onClick: () => dispatch(openModal({
                                    name: 'colorEditor',
                                    data: { id: color.id, name: color.name },
                                })) })))) : (React.createElement("p", null, color.name))),
                        sortVariants(sizes, variants.filter((variant) => variant.color.id === color.id)).map((variant) => {
                            return (variant.isActive && (React.createElement(Cell, { key: variant.id },
                                React.createElement(InputNumber, { name: variant.id, value: variant.quantity, onChange: handleInputChange, loading: savingVariantKeys.has(variant.id), suffix: "szt.", disabled: specificationDetails.state === 'PRODUCTION', error: showErrors && Number(variant.quantity) === 0 }))));
                        }),
                        React.createElement(Cell, null),
                        React.createElement(Cell, null,
                            sumBy(variants.filter((variant) => variant.color.id === color.id), (item) => Number(item.quantity)),
                            ' ',
                            t('pcs_shortcut'))));
                })),
                React.createElement(Row, null,
                    React.createElement(Cell, { span: "full" },
                        React.createElement("div", { style: { display: 'flex', gap: '6px' } },
                            React.createElement(Button, { theme: "border", leftIcon: React.createElement(PlusSvg, null), onClick: () => dispatch(openModal({ name: 'colorCreator' })), disabled: specificationDetails.state === 'PRODUCTION' }, t('add_version')),
                            selectedVariants.length > 0 && (React.createElement(Button, { theme: "border", onClick: () => dispatch(removeModelVariants({ colorIds: selectedVariants }))
                                    .unwrap()
                                    .then(() => setSelectedVariants([]))
                                    .catch((errorMessage) => {
                                    dispatch(addToast({
                                        theme: 'ghost',
                                        color: 'negative',
                                        title: t('common:error'),
                                        description: t(`exceptions:${exceptionMessageToKey(errorMessage)}`, {
                                            defaultValue: errorMessage,
                                        }),
                                    }));
                                }), loading: productionQuantitiesStatus.removeColor === 'loading' }, t('remove_color_versions'))))))),
            React.createElement(TableGrid, { noBg: true, columns: [
                    ['180px', '1fr'],
                    ...sizes
                        .filter((size) => size.isActive)
                        .map(() => ['80px', '150px']),
                    ['40px', '40px'],
                    ['80px', '150px'],
                ] },
                React.createElement(Row, null,
                    React.createElement(Cell, null, t('common:summary')),
                    sizes.map((size) => size.isActive && (React.createElement(Cell, { key: size.id },
                        sumBy(variants.filter((variant) => variant.size.id === size.id), (item) => Number(item.quantity)) || 0,
                        ' ',
                        t('pcs_shortcut')))),
                    React.createElement(Cell, null),
                    React.createElement(Cell, null,
                        sum(sizes.map((size) => sumBy(variants.filter((variant) => variant.size.id === size.id), (item) => Number(item.quantity)) || 0)) || 0,
                        ' ',
                        t('pcs_shortcut'))))))));
}
export default ProductionQuantities;
